// Thin typed wrapper over fetch. Request and response types come from the generated
// OpenAPI schema (npm run api:gen), so every call is checked against the backend.

import type { components, paths } from './schema';
import { tokenStore, type TokenPair } from './tokens';

export const API_BASE = '/api';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

/** Paths that support the given method. */
export type PathsWith<M extends HttpMethod> = {
  [P in keyof paths]: undefined extends paths[P][M] ? never : P;
}[keyof paths];

type Operation<P extends keyof paths, M extends HttpMethod> = NonNullable<paths[P][M]>;

type ParamsOf<O> = O extends { parameters: infer Params } ? Params : never;
type PathParamsOf<O> = ParamsOf<O> extends { path?: infer T } ? NonNullable<T> : never;
type QueryOf<O> = ParamsOf<O> extends { query?: infer T } ? NonNullable<T> : never;
type BodyOf<O> = O extends { requestBody: { content: { 'application/json': infer B } } }
  ? B
  : never;
type JsonOf<R> = R extends { content: { 'application/json': infer J } } ? J : undefined;

/** JSON body of the 200/201 response; `undefined` for 204. */
export type ResponseOf<O> = O extends { responses: infer R }
  ? 200 extends keyof R
    ? JsonOf<R[200]>
    : 201 extends keyof R
      ? JsonOf<R[201]>
      : undefined
  : never;

export type RequestOptions<O> = ([PathParamsOf<O>] extends [never]
  ? { path?: never }
  : { path: PathParamsOf<O> }) &
  ([BodyOf<O>] extends [never] ? { body?: never } : { body: BodyOf<O> }) &
  ([QueryOf<O>] extends [never] ? { query?: never } : { query?: QueryOf<O> }) & {
    signal?: AbortSignal;
    /** This action's id for the server (STAGE_5 5.6): a repeat gets the first answer. */
    actionId?: string;
  };

type Args<O> = object extends RequestOptions<O>
  ? [options?: RequestOptions<O>]
  : [options: RequestOptions<O>];

export class ApiError extends Error {
  readonly status: number;
  /** FastAPI's `detail`, unwrapped: the list of a 422, the {code, ...} of a 409. */
  readonly detail: unknown;
  /** The whole response body, for the rare answer that is not shaped that way. */
  readonly body: unknown;
  readonly headers: Headers;

  constructor(status: number, body: unknown, headers: Headers) {
    super(`API error ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.detail = unwrapDetail(body);
    this.headers = headers;
  }
}

/** Every error the API raises is `{"detail": ...}`; callers want what is inside it. */
function unwrapDetail(body: unknown): unknown {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return body;
  return body.detail;
}

/** The server could not be reached at all. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Network error', { cause });
    this.name = 'NetworkError';
  }
}

interface RawOptions {
  path?: object;
  query?: object;
  body?: unknown;
  /** Sent as it is (a file), with its own type, instead of `body` as JSON. */
  file?: Blob;
  /** The id of this action for the server (STAGE_5 5.6): sent again, it is not done
   * again, and the first answer comes back. */
  actionId?: string;
  signal?: AbortSignal;
}

/** The header the server knows a repeated action by. */
export const ACTION_HEADER = 'X-Client-Action-Id';

function buildUrl(template: string, options: RawOptions): string {
  const pathParams = (options.path ?? {}) as Record<string, unknown>;
  const path = template.replace(/\{(\w+)\}/g, (_, name: string) =>
    encodeURIComponent(String(pathParams[name])),
  );
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value === undefined || value === null) continue;
    for (const item of Array.isArray(value) ? (value as unknown[]) : [value]) {
      search.append(key, String(item));
    }
  }
  const query = search.toString();
  return `${API_BASE}${path}${query ? `?${query}` : ''}`;
}

// Auth endpoints never trigger a token refresh on 401.
const NO_REFRESH = new Set<string>(['/auth/login', '/auth/refresh', '/auth/logout']);

async function send(
  method: HttpMethod,
  template: string,
  options: RawOptions,
  withAuth: boolean,
): Promise<Response> {
  const headers = new Headers({ Accept: 'application/json' });
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  if (options.file) headers.set('Content-Type', options.file.type || 'application/octet-stream');
  if (options.actionId) headers.set(ACTION_HEADER, options.actionId);
  const access = tokenStore.getAccess();
  if (withAuth && access) headers.set('Authorization', `Bearer ${access}`);
  const init: RequestInit = { method: method.toUpperCase(), headers };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.file) init.body = options.file;
  if (options.signal) init.signal = options.signal;
  try {
    return await fetch(buildUrl(template, options), init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError(error);
  }
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

// --- token refresh ---------------------------------------------------------------------

let refreshInFlight: Promise<boolean> | null = null;

async function postRefresh(refreshToken: string): Promise<TokenPair | null> {
  const response = await send('post', '/auth/refresh', { body: { refresh_token: refreshToken } }, false);
  if (!response.ok) return null;
  return (await parse(response)) as TokenPair;
}

async function rotate(): Promise<boolean> {
  const used = tokenStore.getRefresh();
  if (!used) return false;
  let pair = await postRefresh(used);
  if (!pair) {
    // Another tab may have rotated the token meanwhile: retry once with the new one.
    const current = tokenStore.getRefresh();
    if (current && current !== used) pair = await postRefresh(current);
  }
  if (!pair) {
    tokenStore.clear(true);
    return false;
  }
  tokenStore.set(pair);
  return true;
}

async function rotateExclusive(): Promise<boolean> {
  // Refresh tokens are single-use: tabs of the same browser must not rotate in parallel.
  if ('locks' in navigator) {
    return navigator.locks.request('sos-token-refresh', rotate);
  }
  return rotate();
}

/** Get a new token pair; concurrent callers share one request. False when the session ended. */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= rotateExclusive()
    .catch((error: unknown) => {
      // A network failure is not a logout: keep the refresh token for a later retry.
      if (error instanceof NetworkError) throw error;
      tokenStore.clear(true);
      return false;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

// --- public API ------------------------------------------------------------------------

async function request<M extends HttpMethod, P extends PathsWith<M>>(
  method: M,
  path: P,
  ...[options]: Args<Operation<P, M>>
): Promise<ResponseOf<Operation<P, M>>> {
  const raw = (options ?? {}) as RawOptions;
  const template = path as string;
  const withAuth = !NO_REFRESH.has(template);

  let response = await send(method, template, raw, withAuth);
  if (response.status === 401 && tokenStore.isImpersonating()) {
    // The borrowed session is over (it ran out, or the vendor's access was switched
    // off): back to the vendor's own, from a clean page.
    tokenStore.endImpersonation();
    window.location.assign('/');
  } else if (response.status === 401 && withAuth && tokenStore.getRefresh()) {
    if (await refreshSession()) response = await send(method, template, raw, withAuth);
  }
  const data = await parse(response);
  if (!response.ok) throw new ApiError(response.status, data, response.headers);
  return data as ResponseOf<Operation<P, M>>;
}

/** A file the API sends (the journals' Excel): its bytes and the name it suggests.
 * Same auth and refresh as `request`; the body is read as bytes, not JSON. */
async function downloadFile<P extends PathsWith<'get'>>(
  path: P,
  ...[options]: Args<Operation<P, 'get'>>
): Promise<{ blob: Blob; filename: string }> {
  const raw = (options ?? {}) as RawOptions;
  const template = path as string;
  let response = await send('get', template, raw, true);
  if (response.status === 401 && tokenStore.getRefresh()) {
    if (await refreshSession()) response = await send('get', template, raw, true);
  }
  if (!response.ok) throw new ApiError(response.status, await parse(response), response.headers);
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'download';
  return { blob: await response.blob(), filename };
}

export type StoredFile = components['schemas']['FileOut'];

/** Keep an image on the server (POST /files, STAGE_5 5.4): the body is the file itself.
 * Same auth and refresh as `request`. */
async function uploadFile(
  file: Blob,
  purpose: 'signature' | 'receipt' | 'incident' | 'logo' | 'icon',
  actionId?: string,
): Promise<StoredFile> {
  const raw: RawOptions = { query: { purpose }, file, ...(actionId ? { actionId } : {}) };
  let response = await send('post', '/files', raw, true);
  if (response.status === 401 && tokenStore.getRefresh()) {
    if (await refreshSession()) response = await send('post', '/files', raw, true);
  }
  const data = await parse(response);
  if (!response.ok) throw new ApiError(response.status, data, response.headers);
  return data as StoredFile;
}

export type SheetImport = components['schemas']['SheetImportOut'];
export type SheetKind = SheetImport['kind'];

/** An Excel sheet of the first run's wizard (STAGE_7 7.9), sent as it is: checked, or
 * applied. Same auth and refresh as `request`. */
async function importSheet(kind: SheetKind, step: 'check' | 'apply', file: Blob): Promise<SheetImport> {
  const template = step === 'check' ? '/imports/{kind}/check' : '/imports/{kind}/apply';
  const raw: RawOptions = { path: { kind }, file };
  let response = await send('post', template, raw, true);
  if (response.status === 401 && tokenStore.getRefresh()) {
    if (await refreshSession()) response = await send('post', template, raw, true);
  }
  const data = await parse(response);
  if (!response.ok) throw new ApiError(response.status, data, response.headers);
  return data as SheetImport;
}

export type Prompt = components['schemas']['PromptOut'];

/** A telephony announcement (STAGE_T T.4), the WAV or MP3 sent as it is; the server makes
 * it the pbx's A-law. Same auth and refresh as `request`. */
async function uploadPrompt(kind: Prompt['kind'], lang: Prompt['lang'], file: Blob): Promise<Prompt> {
  const template = '/telephony/prompts/{kind}/{lang}';
  const raw: RawOptions = { path: { kind, lang }, file };
  let response = await send('put', template, raw, true);
  if (response.status === 401 && tokenStore.getRefresh()) {
    if (await refreshSession()) response = await send('put', template, raw, true);
  }
  const data = await parse(response);
  if (!response.ok) throw new ApiError(response.status, data, response.headers);
  return data as Prompt;
}

/** Where a stored file is shown from: its token is the whole key. */
export function fileUrl(token: string): string {
  return `${API_BASE}/files/${encodeURIComponent(token)}`;
}

export const api = {
  download: downloadFile,
  uploadFile,
  importSheet,
  uploadPrompt,
  get: <P extends PathsWith<'get'>>(path: P, ...args: Args<Operation<P, 'get'>>) =>
    request('get', path, ...args),
  post: <P extends PathsWith<'post'>>(path: P, ...args: Args<Operation<P, 'post'>>) =>
    request('post', path, ...args),
  put: <P extends PathsWith<'put'>>(path: P, ...args: Args<Operation<P, 'put'>>) =>
    request('put', path, ...args),
  patch: <P extends PathsWith<'patch'>>(path: P, ...args: Args<Operation<P, 'patch'>>) =>
    request('patch', path, ...args),
  delete: <P extends PathsWith<'delete'>>(path: P, ...args: Args<Operation<P, 'delete'>>) =>
    request('delete', path, ...args),
};

export type Schemas = components['schemas'];
